import React, { useEffect, useRef } from 'react';
import { Trophy, Skull, Target, Swords, TrendingDown, Clock } from 'lucide-react';

/**
 * DefeatScreen - Shows when player surrenders or is eliminated
 * Displays game stats and allows player to spectate, start new game, or return to lobby
 */
export default function DefeatScreen({ game, playerCountry, onNewGame, onClose, onSpectate }) {
  // ✅ NULL SAFETY: Provide fallback values if game/playerCountry is null
  const stats = {
    turnsPlayed: game?.current_turn || 0,
    enemiesDefeated: playerCountry?.victory_stars || 0,
    population: playerCountry?.resources?.population || 0,
    money: playerCountry?.resources?.money || 0,
    influence: playerCountry?.resources?.influence || 0,
    military: playerCountry?.resources?.military || 0
  };

  // ✅ NEW: Play "Thanks for Playing Commander" audio on mount
  const audioRef = useRef(null);
  
  useEffect(() => {
    // Play audio when defeat screen appears
    const audio = new Audio('/assets/audio/thanks-for-playing.mp3');
    audio.volume = 0.7; // 70% volume to layer with defeat music
    audio.play().catch(e => console.log('Thanks for playing audio failed:', e));
    audioRef.current = audio;
    
    console.log('🎵 Playing "Thanks for Playing Commander" on defeat screen');
    
    // Cleanup
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
      <div className="relative max-w-2xl w-full mx-4 bg-gradient-to-br from-red-900 to-gray-900 rounded-2xl border-4 border-red-600 shadow-2xl overflow-hidden">
        
        {/* Skull background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="flex flex-wrap gap-8 p-8">
            {[...Array(20)].map((_, i) => (
              <Skull key={`skull-bg-${i}-${Date.now()}`} className="w-16 h-16" />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="relative p-8 text-center">
          {/* Title */}
          <div className="mb-8">
            <Skull className="w-24 h-24 mx-auto text-red-500 mb-4 animate-pulse" />
            <h1 className="text-5xl font-bold text-red-500 mb-2" style={{
              textShadow: '0 0 20px rgba(239, 68, 68, 0.8), 0 0 40px rgba(239, 68, 68, 0.4)'
            }}>
              YOU HAVE BEEN DEFEATED
            </h1>
            <p className="text-gray-400 text-lg">
              {playerCountry?.name || 'Your nation'} has fallen
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-black/40 rounded-lg p-4 border border-red-800/50">
              <Clock className="w-8 h-8 mx-auto mb-2 text-blue-400" />
              <div className="text-3xl font-bold text-white">{stats.turnsPlayed}</div>
              <div className="text-sm text-gray-400">Turns Survived</div>
            </div>

            <div className="bg-black/40 rounded-lg p-4 border border-red-800/50">
              <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
              <div className="text-3xl font-bold text-white">{stats.enemiesDefeated}</div>
              <div className="text-sm text-gray-400">Enemies Defeated</div>
            </div>

            <div className="bg-black/40 rounded-lg p-4 border border-red-800/50">
              <Target className="w-8 h-8 mx-auto mb-2 text-green-400" />
              <div className="text-3xl font-bold text-white">{stats.population}M</div>
              <div className="text-sm text-gray-400">Final Population</div>
            </div>

            <div className="bg-black/40 rounded-lg p-4 border border-red-800/50">
              <Swords className="w-8 h-8 mx-auto mb-2 text-red-400" />
              <div className="text-3xl font-bold text-white">{stats.military}</div>
              <div className="text-sm text-gray-400">Final Military</div>
            </div>
          </div>

          {/* Final Resources */}
          <div className="bg-black/40 rounded-lg p-4 mb-8 border border-red-800/50">
            <div className="text-sm text-gray-400 mb-2">Final Resources</div>
            <div className="flex justify-around text-sm">
              <div>
                <span className="text-green-400">💰 ${stats.money}M</span>
              </div>
              <div>
                <span className="text-blue-400">🗣️ {stats.influence} Influence</span>
              </div>
            </div>
          </div>

          {/* Quote */}
          <div className="italic text-gray-400 mb-8 text-lg">
            "In the game of global thermonuclear war, sometimes the only winning move is not to play."
          </div>

          {/* Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={onSpectate}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 border-2 border-blue-400 shadow-lg"
            >
              👁️ Continue Spectating
            </button>
            
            <button
              onClick={onNewGame}
              className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 border-2 border-green-400 shadow-lg"
            >
              🔄 Start New Game
            </button>
            
            <button
              onClick={onClose}
              className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-500 hover:to-gray-600 text-white font-bold py-3 px-12 rounded-lg transition-all transform hover:scale-105 border-2 border-gray-400 shadow-lg"
            >
              🏠 Return to Lobby
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
